/*
* This module is the application's main script.
*
* The code here should be only that which is strictly necessary for all/most pages of the application
*
*/

import {SearchFilter} from "./SearchFilter.js"
import {ResourceLoader} from "./ResourceLoader.js"
import {NoticeMessage} from "../components/NoticeMessage.js";

// we're going to use this component on almost any page, so initialize it at the application-level
NoticeMessage.init()

// Tracks which submit button triggered each form submission so we can target it with the loading state
const submittingButtons = new WeakMap<HTMLFormElement, HTMLButtonElement>();

document.addEventListener("click", (e) => {
    const button = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>('[type="submit"]');
    if (!button) return;
    const form = button.closest("form");
    if (form) submittingButtons.set(form, button);
});

document.addEventListener("submit", (e) => {
    const form = e.target as HTMLFormElement;
    const button = submittingButtons.get(form) ?? form.querySelector<HTMLButtonElement>('[type="submit"]');
    if (!button) return;
    // Defer so the browser collects form data (including this button's name/value) before it is disabled.
    // Disabled fields are excluded from form data, which would break server-side authorization checks that depend on the submitted button value.
    setTimeout(() => {
        button.disabled = true;
        button.insertAdjacentHTML("afterbegin", '<i data-loading class="fa-solid fa-spinner fa-spin"></i>');

        // For download buttons we don't navigate away from the page, so we need a provision for re-enabling the button after the download is complete.
        const enableAfterMs = button.dataset.enableAfterTimeout;
        if (enableAfterMs) {
            setTimeout(() => {
                button.disabled = false;
                button.querySelector<HTMLElement>("i[data-loading]")?.remove();
            }, parseInt(enableAfterMs));
        }
    }, 0);
});

// Prevent the page from scrolling (and closing the popup) when the user wheels inside any Kendo dropdown popup.
document.addEventListener("wheel", (e) => {
    const popup = (e.target as HTMLElement | null)?.closest<HTMLElement>(".k-list-container");
    if (!popup) return;

    const scroller = popup.querySelector<HTMLElement>(".k-list-scroller");
    if (!scroller) return;

    // If hovering on option label or other non-scrollable popup area (like the "- Select One -" option or a search bar in the dropdown) — Manually apply the scroll rather than just closing the popup.
    if (!(e.target as HTMLElement | null)?.closest(".k-list-scroller")) {
        scroller.scrollBy({top: e.deltaY, behavior: "instant"});
        e.preventDefault();
        e.stopPropagation();
        return;
    }

    // Prevent scrolling if at the top or bottom of the list. This stops the popup from closing when scrolling.
    const atTop = scroller.scrollTop === 0 && e.deltaY < 0;
    const atBottom = Math.ceil(scroller.scrollTop) >= scroller.scrollHeight - scroller.offsetHeight && e.deltaY > 0;
    if (atTop || atBottom) {
        e.preventDefault();
        e.stopPropagation();
    }
}, {passive: false});

$(() => {
    new SearchFilter()

    /* Load Resources */
    ResourceLoader.Init(document.documentElement.lang)
        .then((resources) => {
            if (resources === null) {
                console.log("Failed to load resources")
            } else {
                window.Res = resources.GetResources()
            }
        })
        .catch((_) => {
            console.log("Failed to load resources")
        })
})
